<template>
  <div class="acc-list">
    <div class="acc-list__head">
      <div class="acc-list__hint">{{ hint }}</div>
      <v-text-field
        v-model="search"
        class="acc-list__search"
        :placeholder="$t('settingSearch')"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        hide-details
        clearable
      />
    </div>

    <div class="acc-list__body">
      <v-progress-linear v-if="loading" indeterminate color="primary" class="acc-list__loader" />
      <div v-if="!loading && !filtered.length" class="acc-list__empty">
        <v-icon large class="mb-2">mdi-text-box-search-outline</v-icon>
        <div>{{ $t('settingNoResult') }}</div>
      </div>
      <div
        v-for="row in filtered"
        :key="row.index"
        class="acc-list__row"
        :class="{ 'acc-list__row--set': !!row.item.given }"
      >
        <span class="acc-list__num">{{ row.index + 1 }}</span>
        <div class="acc-list__name">
          <div class="acc-list__name-text">{{ $t(row.item.name) }}</div>
          <div class="acc-list__status">
            <v-icon x-small :color="row.item.given ? 'success' : 'grey'">
              {{ row.item.given ? 'mdi-check-circle' : 'mdi-circle-outline' }}
            </v-icon>
            {{ row.item.given ? $t('accountingGivenSet') : $t('accountingGivenNotSet') }}
          </div>
        </div>
        <div class="acc-list__field">
          <setting-account-picker v-model="row.item.given" type="given" variant="soft" :placeholder="$t('accountingSelectGiven')" new-btn />
        </div>
      </div>
    </div>

    <div class="acc-list__foot">
      <v-btn color="primary" depressed class="app-form-action-btn" :loading="saving" @click="save">
        <v-icon left small>mdi-content-save-outline</v-icon>{{ $t('submit') }}
      </v-btn>
      <span class="acc-list__progress">
        {{ $t('accountingGivenProgress', { n: setCount, total: items.length }) }}
      </span>
      <v-progress-linear
        class="acc-list__bar"
        :value="items.length ? (setCount / items.length) * 100 : 0"
        rounded
        height="6"
        color="success"
      />
    </div>

    <v-snackbar v-model="snakbar" :color="snakbarColor || 'primary'" top style="z-index: 1000000000000 !important">
      {{ snakbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snakbar = false">{{ $t('close') }}</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
// Replaces accountingGivenConfig / accountingGeneralGivenConfig /
// accountingKindProductGiven: same endpoints, one card-list UI.
import General from '../../../../../Mixins/general'
import SettingAccountPicker from '../components/SettingAccountPicker'

export default {
  name: 'AccountingGivenList',
  components: { SettingAccountPicker },
  mixins: [General],
  props: {
    // getdetail/createdetail "select" value
    select: { type: String, required: true },
    // optional "type" filter (invoice | entrepot)
    typeProp: { type: String, default: null },
    hint: { type: String, default: '' }
  },
  data: () => ({
    items: [],
    search: '',
    loading: false,
    saving: false
  }),
  computed: {
    filtered () {
      const q = (this.search || '').trim().toLowerCase()
      return this.items
        .map((item, index) => ({ item, index }))
        .filter(r => !q || String(this.$t(r.item.name)).toLowerCase().includes(q))
    },
    setCount () {
      return this.items.filter(i => !!i.given).length
    }
  },
  watch: {
    typeProp () {
      this.getItems()
    }
  },
  mounted () {
    this.getItems()
  },
  methods: {
    getItems () {
      this.loading = true
      const params = { select: this.select }
      if (this.typeProp) params.type = this.typeProp
      this.$axios.get(this.$endpoint + 'getdetail', { params })
        .then(res => {
          this.items = res.data || []
        })
        .catch(err => this.serverErrorHandeler(err))
        .finally(() => { this.loading = false })
    },
    save () {
      this.saving = true
      const formData = new FormData()
      formData.append('select', this.select)
      formData.append('items', JSON.stringify(this.items))
      this.$axios.post(this.$endpoint + 'createdetail', formData)
        .then(() => {
          this.snakbarText = this.$t('alert-created')
          this.snakbarColor = 'green'
          this.snakbar = true
          this.getItems()
        })
        .catch(err => this.serverErrorHandeler(err))
        .finally(() => { this.saving = false })
    }
  }
}
</script>

<style scoped>
.acc-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.acc-list__head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 18px 14px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.07);
}
.acc-list__head ::v-deep .v-input {
  height: auto !important;
}
.acc-list__hint {
  flex: 1 1 auto;
  font-size: 0.8rem;
  line-height: 1.6;
  opacity: 0.6;
}
.acc-list__search {
  flex: 0 0 260px;
}
.acc-list__body {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 14px 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.acc-list__loader {
  position: sticky;
  top: 0;
  z-index: 1;
}
.acc-list__empty {
  padding: 48px 16px;
  text-align: center;
  opacity: 0.55;
  font-size: 0.85rem;
}
/* Rows must not shrink inside the scrolling flex column, or on short
   screens they squash into each other and under the search. */
.acc-list__body > * {
  flex-shrink: 0;
}
.acc-list__row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  background: #fff;
  transition: border-color 0.15s ease;
}
.acc-list__row--set {
  border-color: rgba(46, 125, 50, 0.25);
}
.theme--dark .acc-list__row {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.12);
}
.acc-list__num {
  flex: 0 0 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  background: rgba(15, 23, 42, 0.06);
}
.acc-list__name {
  flex: 0 0 240px;
  min-width: 0;
}
.acc-list__name-text {
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.5;
}
.acc-list__status {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  opacity: 0.65;
  margin-top: 2px;
}
.acc-list__field {
  flex: 1 1 auto;
  min-width: 0;
}
.acc-list__field ::v-deep .v-input {
  margin: 0 !important;
  padding: 0 !important;
}
.acc-list__field ::v-deep .v-text-field__details {
  display: none;
}
.acc-list__foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  border-top: 1px solid rgba(15, 23, 42, 0.07);
  background: #fafbfd;
}
.theme--dark .acc-list__foot {
  background: rgba(255, 255, 255, 0.03);
}
.acc-list__progress {
  font-size: 0.78rem;
  opacity: 0.7;
  white-space: nowrap;
}
.acc-list__bar {
  max-width: 160px;
}

@media (max-width: 799px) {
  .acc-list__head {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 12px;
  }
  .acc-list__search { flex: 0 0 auto; }
  .acc-list__body { padding: 12px 12px 12px; gap: 10px; }
  .acc-list__row {
    flex-wrap: wrap;
    gap: 10px;
  }
  .acc-list__name { flex: 1 1 0; }
  .acc-list__field { flex: 1 1 100%; }
  .acc-list__foot { padding: 10px 12px calc(10px + env(safe-area-inset-bottom)); }
  .acc-list__bar { display: none; }
}
</style>
